import { Box, Button, Typography } from "@mui/material";
import { cityScraperList } from "../../../data/cityScraperList";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  fetchJaxFields,
  setCityFailedErrorText,
  setPermitPanelOpen,
  setScheduleOfValues,
  setUsingTemplates,
} from "../../../store/actions/uiActions/myPermitsActions";
import JaxModal from "../../noc/realEstateComponents/JaxModal";

const CitySiteTab = ({ isReadOnlyPage = false }) => {
  const {
    selectedRow,
    prevRowID,
    cityFailedErrorText,
    selectedPermitType,
    activeTab,
  } = useSelector((state) => state.myPermitsComponent);
  const { permitPanelOpen } = useSelector(
    (state) => state.myPermitsComponent.citySite,
  );

  const dispatch = useDispatch();

  // Get the correct permit data based on selectedPermitType
  const getCurrentPermitData = () => {
    if (!selectedRow || !selectedPermitType) return selectedRow;

    // If it's the main permit type, return main selectedRow
    if (selectedPermitType === selectedRow.permit_type) {
      return selectedRow;
    }

    // If it's an additional permit, find it in additional_permits
    if (
      selectedRow.additional_permits &&
      selectedRow.additional_permits[selectedPermitType]
    ) {
      const additionalPermit =
        selectedRow.additional_permits[selectedPermitType];
      if (Array.isArray(additionalPermit)) {
        // If it's an array, get the first one (most recent)
        return additionalPermit[0];
      } else {
        // If it's a single object
        return additionalPermit;
      }
    }

    return selectedRow;
  };

  const currentPermitData = getCurrentPermitData();
  const autoEmailRows = Array.isArray(currentPermitData?.send_form_to_city)
    ? currentPermitData.send_form_to_city
    : [];
  const autoEmailEnabled = autoEmailRows.length > 0;
  const cityEmailSubmission =
    currentPermitData?.connect_to_city?.city_email_submission || null;

  const toRecipients = [
    ...new Set(autoEmailRows.map((row) => row?.to).filter(Boolean)),
  ];
  const ccRecipients = [
    ...new Set(autoEmailRows.map((row) => row?.from).filter(Boolean)),
  ];
  const isNotarized = Boolean(
    currentPermitData?.notarized ||
      currentPermitData?.noc_notarized ||
      currentPermitData?.pp_notarized ||
      currentPermitData?.processing_permit?.notarization?.finalized,
  );
  const selectedDocs = Array.isArray(currentPermitData?.selected_documents)
    ? currentPermitData.selected_documents
    : [];
  const docLabelByName = selectedDocs.reduce((acc, doc) => {
    if (doc?.name) acc[doc.name] = doc?.label;
    return acc;
  }, {});

  const formatTimestamp = (unixTs) => {
    if (!unixTs) return "-";
    const date = new Date(unixTs * 1000);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  };

  const [animateOut, setAnimateOut] = useState(false);
  const [startCoords, setStartCoords] = useState({ top: 0, left: 0 });

  const buttonRef = useRef(null);

  const handleClosePanel = () => {
    dispatch(
      setScheduleOfValues({
        rows: [],
        scheduleCreated: false,
        remainingAmount: "―",
        remainingPercentage: "―",
        totalCost: 0,
        totalCostDisplay: "",
        editSchedule: false,
        scheduleOfValuesDocLoading: false,
        loadedLogo: "",
        downloadLoading: false,
        savingTemplateLoading: false,
      }),
    );
    dispatch(
      setUsingTemplates({
        isUsingTemplate: false,
        templateID: "",
        templateName: "",
        removedTemplate: false,
      }),
    );
    setAnimateOut(true);
    setTimeout(() => {
      dispatch(setPermitPanelOpen(false));
    }, 600); // Match with animation duration
  };

  const handleOpenPanel = () => {
    const rect = buttonRef.current.getBoundingClientRect();
    setStartCoords({
      top: rect.top + rect.height / 2,
      left: rect.left + rect.width / 2,
    });
    dispatch(setPermitPanelOpen(true));
    setAnimateOut(false);
  };

  const renderedCityFieldsRef = useRef(null);

  useEffect(() => {
    const shouldFetch =
      currentPermitData?.noc_created &&
      currentPermitData?.connect_to_city?.is_connected &&
      currentPermitData?.connect_to_city?.permit_url &&
      currentPermitData?.task_id &&
      currentPermitData.task_id !== prevRowID &&
      !renderedCityFieldsRef?.current;

    if (shouldFetch) {
      renderedCityFieldsRef.current = true;
      dispatch(fetchJaxFields());
    }
  }, [
    currentPermitData?.task_id,
    currentPermitData?.noc_created,
    currentPermitData?.connect_to_city?.is_connected,
    currentPermitData?.connect_to_city?.permit_url,
    prevRowID,
    dispatch,
  ]);

  useEffect(() => {
    if (cityFailedErrorText) {
      dispatch(setCityFailedErrorText(""));
    }
  }, [cityFailedErrorText, dispatch]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          mt: 1,
        }}
      >
        <img
          alt="city_img"
          src="/img/website_img/city_img.png"
          style={{
            width: "100%",
            maxWidth: "5rem",
            height: "auto",
            borderRadius: "20px",
          }}
        />
        <Typography align="center" px={1} variant="h5" mt={1} color="primary">
          City Permitting
        </Typography>

        {autoEmailEnabled && (
          <Box
            mt={2}
            px={2}
            py={1.5}
            sx={{ border: "1px solid #d1d5db", borderRadius: 2 }}
          >
            <Typography variant="body2" fontWeight="bold" color="primary">
              Auto Email to City: Enabled
            </Typography>

            {!!toRecipients.length && (
              <Typography variant="caption" display="block" mt={0.5}>
                To: {toRecipients.join(", ")}
              </Typography>
            )}
            {!!ccRecipients.length && (
              <Typography variant="caption" display="block">
                CC/Reply-to: {ccRecipients.join(", ")}
              </Typography>
            )}

            <Box display="flex" alignItems="center" gap={1} mt={1}>
              {cityEmailSubmission?.status === "sent" ? (
                <CheckCircleIcon sx={{ color: "#10b981", fontSize: "1rem" }} />
              ) : cityEmailSubmission?.status === "failed" ? (
                <CancelIcon sx={{ color: "#ef4444", fontSize: "1rem" }} />
              ) : (
                <CancelIcon sx={{ color: "#f59e0b", fontSize: "1rem" }} />
              )}

              <Typography variant="body2" fontWeight="bold">
                {cityEmailSubmission?.status === "sent"
                  ? "Submission Status: Sent"
                  : cityEmailSubmission?.status === "failed"
                    ? "Submission Status: Failed"
                    : "Submission Status: Pending"}
              </Typography>
            </Box>

            {cityEmailSubmission?.status === "sent" && (
              <>
                <Typography variant="caption" display="block" mt={0.5}>
                  Sent at: {formatTimestamp(cityEmailSubmission?.sent_at)}
                </Typography>
                {!!cityEmailSubmission?.documents?.length && (
                  <Box mt={0.5}>
                    <Typography
                      variant="caption"
                      display="block"
                      fontWeight="bold"
                    >
                      Documents sent:
                    </Typography>
                    {cityEmailSubmission.documents.map((doc, idx) => (
                      <Typography
                        key={`${doc?.name || "doc"}-${idx}`}
                        variant="caption"
                        display="block"
                      >
                        {docLabelByName?.[doc?.name] ||
                          doc?.filename ||
                          doc?.name ||
                          `Document ${idx + 1}`}
                      </Typography>
                    ))}
                  </Box>
                )}
              </>
            )}

            {cityEmailSubmission?.status === "failed" && (
              <Typography
                variant="caption"
                display="block"
                mt={0.5}
                color="error"
              >
                Error:{" "}
                {cityEmailSubmission?.error || "Failed to send city email"}
              </Typography>
            )}

            {!cityEmailSubmission?.status && (
              <Typography variant="caption" display="block" mt={0.5}>
                {isNotarized
                  ? "Notarization is complete. Waiting for city email send confirmation."
                  : "Not sent yet. This will be sent automatically once notarization is complete."}
              </Typography>
            )}
          </Box>
        )}

        {!cityScraperList.includes(selectedRow?.city) && !autoEmailEnabled ? (
          <Box mt={6}>
            <Typography align="center" px={2} variant="h6" color="primary">
              Unfortunately, we do not currently support permitting automation
              for this city. Please check back later for future updates.
            </Typography>
          </Box>
        ) : cityScraperList.includes(selectedRow?.city) &&
          !currentPermitData?.connect_to_city?.permit?.permit_id ? (
          <Box mt={6}>
            <Typography align="center" px={2} variant="h6" color="primary">
              Permit creation requests have not yet been initiated.
            </Typography>
          </Box>
        ) : cityScraperList.includes(selectedRow?.city) &&
          currentPermitData?.connect_to_city?.permit?.permit_id ? (
          <>
            <Typography
              align="center"
              px={1}
              mb={1}
              variant="body2"
              fontWeight="bold"
              color="primary"
            >
              {currentPermitData?.connect_to_city?.permit?.full_permit_number}
            </Typography>
            {currentPermitData?.connect_to_city?.status &&
              Object.entries(currentPermitData.connect_to_city.status).map(
                ([key, value], idx) => {
                  const label = key
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");

                  return (
                    <Box
                      key={idx}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                      px={2}
                      py={0}
                    >
                      {value ? (
                        <CheckCircleIcon
                          sx={{ color: "#10b981", fontSize: "1rem" }}
                        />
                      ) : (
                        <CancelIcon
                          sx={{ color: "#ef4444", fontSize: "1rem" }}
                        />
                      )}
                      <Typography variant="body2" fontWeight="bold">
                        {label}
                      </Typography>
                    </Box>
                  );
                },
              )}
            {activeTab !== 3 &&
              activeTab !== 4 &&
              !currentPermitData?.notarized &&
              !currentPermitData?.noc_notarized &&
              !currentPermitData?.pp_notarized &&
              !currentPermitData?.processing_permit?.notarization?.started &&
              !isReadOnlyPage && (
                <Button
                  ref={buttonRef}
                  variant="contained"
                  sx={{ mt: 2, mb: 1 }}
                  size="small"
                  color="primary"
                  onClick={handleOpenPanel}
                >
                  Open City Permit Panel
                </Button>
              )}
            <Button
              variant="contained"
              size="small"
              color="success"
              href={currentPermitData?.connect_to_city?.permit_url}
              target="_blank"
            >
              Visit City Permitting Application
            </Button>
          </>
        ) : null}
      </Box>
      {permitPanelOpen && (
        <JaxModal
          handleClosePanel={handleClosePanel}
          startCoords={startCoords}
          animateOut={animateOut}
        />
      )}
    </>
  );
};

export default React.memo(CitySiteTab);
